import { HeaderIcons } from './icons';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from '../../i18n';
import Header from './Header';
import LoadingSpinner from './LoadingSpinner';
import { getPlayerHeaderLeftButton } from './getPlayerHeaderLeftButton';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import RoomSessionStorage from '../../utils/sessionStorage';
import { emitRoomRestore } from '../../utils/restoreRoomSession';

interface LoadingQuestionProps {
  socket: WebSocketAdapter | null;
  roomName: string;
  onRefresh?: () => void;
  onGoHome?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
  /** Called after 10s with no useful response – parent can try recovery (e.g. return-to-waiting / get-room-state) then go home if still stuck */
  onLongWaitTimeout?: () => void;
  title?: string;
  subtitle?: string;
}

// Throttle room-state requests to prevent spam
const ROOM_STATE_REQUEST_THROTTLE_MS = 5000; // Only request once per 5 seconds
const INITIAL_REQUEST_DELAY_MS = 3000; // Initial delay before first request
const RETRY_INTERVAL_MS = 10000; // If still loading, retry every 10 seconds
/** After this long with no response, trigger onLongWaitTimeout so parent can recover or go home */
const LONG_WAIT_TIMEOUT_MS = 10000;

const LoadingQuestion: React.FC<LoadingQuestionProps> = ({ 
  socket, 
  roomName, 
  onRefresh,
  onGoHome,
  isHost = false,
  onBackToGameSelection,
  onLongWaitTimeout,
  title,
  subtitle
}) => {
  const { t } = useTranslation();
  const displayTitle = title ?? t('gameplay.loading.question');
  const lastRequestTimeRef = useRef<number>(0);
  const requestIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const longWaitFiredRef = useRef<boolean>(false);
  const [showLongWaitMessage, setShowLongWaitMessage] = useState(false);
  
  const requestRoomState = () => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTimeRef.current;
    
    // Throttle: only request if enough time has passed
    if (timeSinceLastRequest < ROOM_STATE_REQUEST_THROTTLE_MS) {
      console.log(`⏸️ Throttling room-state request (${timeSinceLastRequest}ms ago, need ${ROOM_STATE_REQUEST_THROTTLE_MS}ms)`);
      return;
    }
    
    if (socket && roomName) {
      const savedData = RoomSessionStorage.getSavedRoomData();
      if (savedData.roomName === roomName) {
        lastRequestTimeRef.current = now;
        console.log('Loading question - requesting room state');
        emitRoomRestore(socket, savedData, { roomName });
      }
    }
  };
  
  // Request room-state after initial delay, then retry periodically if still loading
  useEffect(() => {
    // Clear any existing interval
    if (requestIntervalRef.current) {
      clearInterval(requestIntervalRef.current);
    }
    
    // Show "taking too long" message after 5 seconds (backend throttles to 5s, so this is reasonable)
    const longWaitTimeout = setTimeout(() => {
      setShowLongWaitMessage(true);
    }, 5000);

    // After 10s with no response, give parent a chance to recover (e.g. return-to-waiting / get-room-state) or go home
    const longWaitCallbackTimeout = setTimeout(() => {
      if (!longWaitFiredRef.current && onLongWaitTimeout) {
        longWaitFiredRef.current = true;
        onLongWaitTimeout();
      }
    }, LONG_WAIT_TIMEOUT_MS);
    
    // Initial request after delay
    const initialTimeout = setTimeout(() => {
      requestRoomState();
      
      // Set up periodic retries (every 10 seconds) if still loading
      requestIntervalRef.current = setInterval(() => {
        requestRoomState();
      }, RETRY_INTERVAL_MS);
    }, INITIAL_REQUEST_DELAY_MS);

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(longWaitTimeout);
      clearTimeout(longWaitCallbackTimeout);
      if (requestIntervalRef.current) {
        clearInterval(requestIntervalRef.current);
        requestIntervalRef.current = null;
      }
    };
  }, [socket, roomName, onLongWaitTimeout]);

  const handleRefresh = () => {
    // Manual refresh - bypass throttle for user-initiated action
    if (socket && roomName) {
      const savedData = RoomSessionStorage.getSavedRoomData();
      if (savedData.roomName === roomName) {
        lastRequestTimeRef.current = Date.now(); // Update last request time
        console.log('Manual refresh - requesting room state');
        emitRoomRestore(socket, savedData, { roomName, force: true });
      }
    }
    
    // Also call custom refresh handler if provided
    if (onRefresh) {
      onRefresh();
    }
    
    // Also reload page as fallback
    window.location.reload();
  };

  return (
    <div className="app">
      <Header
        leftButton={getPlayerHeaderLeftButton({
          isHost,
          onBackToGameSelection,
          onGoHome,
          backTooltip: t('gameplay.backToGameSelection'),
          homeTooltip: t('common.tooltips.goHome'),
        })}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: handleRefresh,
          tooltip: t('common.tooltips.refreshPage')
        }}
        roomCode={roomName}
      />
      <div className="container" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: 'calc(100vh - 100px)',
        paddingTop: '100px'
      }}>
        <LoadingSpinner />
        <h1 className="title" style={{ 
          color: 'var(--color-primary)',
          marginBottom: subtitle ? '0.5rem' : '0'
        }}>
          {displayTitle}
        </h1>
        {subtitle && (
          <p className="subtitle" style={{ 
            color: 'rgba(var(--color-primary-rgb), 0.85)',
            fontSize: '1.1rem'
          }}>
            {subtitle}
          </p>
        )}
        {showLongWaitMessage && (
          <p style={{ 
            marginTop: '1rem',
            color: 'rgba(var(--color-primary-rgb), 0.9)',
            fontSize: '1rem',
            textAlign: 'center',
            maxWidth: '400px',
            fontWeight: 'bold'
          }}>
            {t('gameplay.loading.takingLonger')}
          </p>
        )}
        <p style={{ 
          marginTop: showLongWaitMessage ? '0.5rem' : '1rem',
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '0.9rem',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          {showLongWaitMessage 
            ? t('gameplay.loading.longWaitHint')
            : t('gameplay.loading.waitHint')
          }
        </p>
      </div>
    </div>
  );
};

export default LoadingQuestion;
